import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GitFSStatus, GitQuery } from '@y42/git';
import { STORAGE_ID } from '@y42/http';
import { GitHttpHeader } from '@y42/model';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { combineLatest, filter, map, Observable, switchMap, take } from 'rxjs';

export function setGitPathHttpHeader(path: string): HttpHeaders {
  return new HttpHeaders({
    [GitHttpHeader.Path]: encodeURI(path),
  });
}

export function setGitLocationHttpHeader(path: string): HttpHeaders {
  return new HttpHeaders({
    [GitHttpHeader.Location]: encodeURI(path),
  });
}

@Injectable()
export class GitHttpHeaderInterceptor implements HttpInterceptor {
  constructor(private readonly gitQuery: GitQuery, private readonly orgSpaceQuery: OrgSpaceQuery) {}

  private readonly whiteListUrls: RegExp[] = [
    /api.*\.y42/i, // Match any Y42 api endpoint
  ];

  private readonly blockingGitStatuses: Array<GitFSStatus['status']> = ['checkout', 'pull', 'push'];
  private readonly gitHeaders$: Observable<Partial<Record<GitHttpHeader, string | null>>> = combineLatest([
    this.orgSpaceQuery.active$,
    this.gitQuery.gitStatus$.pipe(
      filter((status) => !status || !this.blockingGitStatuses.includes(status.status)),
      switchMap(() => combineLatest([this.gitQuery.activeBranchName$, this.gitQuery.currentLocalCommitSha$])),
    ),
  ]).pipe(
    map(([space, [activeBranchName, commitSha]]) => ({
      [GitHttpHeader.Type]: 'githubapp',
      [GitHttpHeader.Id]: space?.id || '',
      [GitHttpHeader.Branch]: encodeURI(activeBranchName),
      [GitHttpHeader.Commit]: commitSha,
    })),
  );

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const headersWithStorageId = (headers: HttpHeaders) => {
      const storageId = req.context.get(STORAGE_ID);
      return storageId ? headers.set(GitHttpHeader.Id, storageId) : headers;
    };

    const activeBranchName = this.gitQuery.getActiveBranchName();
    if (!activeBranchName) {
      return next.handle(req.clone({ headers: headersWithStorageId(req.headers) }));
    }

    return this.gitHeaders$.pipe(
      take(1),
      switchMap((gitHeaders) => {
        const headers = Object.entries(gitHeaders)
          .filter(([_, value]) => value)
          .reduce((agg, [header, value]) => agg.set(header, value!), req.headers);

        if (!this.whiteListUrls.find((regExp) => regExp.test(req.url))) {
          return next.handle(req);
        }

        return next.handle(req.clone({ headers: headersWithStorageId(headers) }));
      }),
    );
  }
}
